import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { logDataError, logNotFound } from "../lib/errors";
import { changeMyRole } from "../lib/plans";
import { Spinner } from "../components/ui";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

// Datos que el resto de los usuarios pueden ver para elegir destinatario.
function publicProfile(profile) {
  return {
    uid: profile.uid,
    nombre: profile.nombre,
    apellido: profile.apellido,
    email: profile.email,
    rol: profile.rol,
    empresa: profile.empresa,
  };
}

// Lee usuarios/{uid}. Si no existe (cuentas registradas cuando Firestore no guardaba), lo crea.
// También publica perfiles/{uid}: los datos que ven tus contactos (firestore.rules no deja que los vea nadie más).
async function loadProfile(user) {
  const userRef = doc(db, "usuarios", user.uid);
  let data = null; // null = no se pudo leer
  let exists = false;
  try {
    const snapshot = await getDoc(userRef);
    exists = snapshot.exists();
    data = exists ? snapshot.data() : {};
    if (!exists) logNotFound(`leer usuarios/${user.uid}`, "la cuenta no tiene documento de perfil; se crea ahora.");
  } catch (error) {
    logDataError(`leer usuarios/${user.uid}`, error);
  }

  // Si no hay datos en Firestore, el nombre de la cuenta de Auth sirve de respaldo.
  const [authNombre = "", ...authApellido] = (user.displayName || "").split(" ");
  const stored = data || {};
  const profile = {
    uid: user.uid,
    email: user.email,
    nombre: stored.nombre || authNombre,
    apellido: stored.apellido || authApellido.join(" "),
    whatsapp: stored.whatsapp || "",
    empresa: stored.empresa || "",
    rol: stored.rol || "odontologo",
  };

  // Si la lectura falló no escribimos nada: podríamos pisar datos buenos con datos vacíos.
  if (data === null) return profile;

  await Promise.all([
    exists ||
      setDoc(userRef, { ...profile, creadoEn: serverTimestamp() }).catch((error) =>
        logDataError(`crear usuarios/${user.uid}`, error)
      ),
    setDoc(doc(db, "perfiles", user.uid), publicProfile(profile), { merge: true }).catch((error) =>
      logDataError(`publicar perfiles/${user.uid}`, error)
    ),
  ]);

  return profile;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const signingUp = useRef(false);

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      // Durante el registro, signup() carga el perfil cuando ya está guardado.
      if (signingUp.current) return;
      const nextProfile = user ? await loadProfile(user) : null;
      setProfile(nextProfile);
      setCurrentUser(user);
      setLoading(false);
    });
  }, []);

  const signup = useCallback(async ({ email, password, nombre, apellido, whatsapp, empresa, rol }) => {
    signingUp.current = true;
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      const data = { uid: user.uid, email, nombre, apellido, whatsapp, empresa, rol };
      await updateProfile(user, { displayName: `${nombre} ${apellido}`.trim() });
      try {
        await setDoc(doc(db, "usuarios", user.uid), { ...data, creadoEn: serverTimestamp() });
        await setDoc(doc(db, "perfiles", user.uid), publicProfile(data));
      } catch (error) {
        logDataError(`guardar el perfil de ${user.uid} al registrarse`, error);
        throw error;
      }
      setProfile(data);
      setCurrentUser(user);
    } catch (error) {
      // Si la cuenta se creó pero falló guardar los datos, igual dejamos la sesión iniciada.
      if (auth.currentUser) {
        setProfile(await loadProfile(auth.currentUser));
        setCurrentUser(auth.currentUser);
      }
      throw error;
    } finally {
      signingUp.current = false;
    }
  }, []);

  const login = useCallback((email, password) => signInWithEmailAndPassword(auth, email, password), []);

  const logout = useCallback(() => signOut(auth), []);

  const resetPassword = useCallback((email) => sendPasswordResetEmail(auth, email), []);

  const updateProfileData = useCallback(
    async (changes) => {
      const next = { ...profile, ...changes };
      // El rol lo cambia el servidor (también ajusta el plan). Después se guarda el resto con el rol ya igual.
      if (next.rol !== profile.rol) await changeMyRole(next.rol);
      await setDoc(doc(db, "usuarios", profile.uid), next, { merge: true });
      await setDoc(doc(db, "perfiles", profile.uid), publicProfile(next), { merge: true });
      if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: `${next.nombre} ${next.apellido}`.trim() });
      setProfile(next);
    },
    [profile]
  );

  const value = useMemo(
    () => ({ currentUser, profile, signup, login, logout, resetPassword, updateProfileData }),
    [currentUser, profile, signup, login, logout, resetPassword, updateProfileData]
  );

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
        <img src="/assets/isotipo.png" alt="" className="h-14 w-auto" />
        <Spinner className="h-5 w-5 text-brand-600" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
